import { CaretLeft, X } from '@ui/components/icons';
import { Portal } from '@ui/lib/park/portal';
import { Drawer } from '@ui/lib/park/ui/drawer';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { PayablePoolToken } from '@ui/types/token';
import { OrderType } from '@ui/types/trade';
import { getLocalStorageKey } from '@ui/utils/getLocalStorageKey';
import { useTranslation } from 'next-i18next';
import {
  cloneElement,
  forwardRef,
  ReactElement,
  ReactNode,
  Ref,
  useState,
} from 'react';
import { Box, Grid, HStack, HstackProps, Spacer } from 'styled-system/jsx';

import { useMarketStatus } from '../../hooks/useMarketStatus';
import { useTradingPair } from '../../store/useTradingPair';
import { MarketStatusMessage } from '../TradeModule/MarketStatusMessage';
import { RealTimeFees } from '../TradeModule/OpenPositionFees/Fees';
import { useCollateralToken } from '../TradeModule/store/useCollateralToken';
import { useIsLong } from '../TradeModule/store/useIsLong';
import { useOrderType } from '../TradeModule/store/useOrderType';
import { Collateral } from './Collateral';
import { ConfirmChart } from './ConfirmChart/ConfirmChart';
import { ConfirmTradeButton } from './ConfirmTradeButton';
import { ConfirmTradeIntroModal } from './ConfirmTradeIntroModal';
import { EntryPrice } from './EntryPrice';
import { useOpenLimitOrder } from './hooks/useOpenLimitOrder';
import { useOpenMarketTrade } from './hooks/useOpenMarketTrade';
import { useTradeModuleContext } from './hooks/useTradeModuleContext';
import { Leverage } from './Leverage';
import { LiqPrice } from './LiqPrice';
import { MarkPrice } from './MarkPrice';
import { PositionSize } from './PositionSize';
import { StopLossAt } from './StopLossAt';
import { TakeProfitAt } from './TakeProfitAt';

type ConfirmTradeIntroVariant = 'USDC-LVUSD' | 'MON-LVMON';

const getConfirmTradeIntroVariant = (
  token: PayablePoolToken | undefined,
): ConfirmTradeIntroVariant | null => {
  // Per spec: only show for USDC or MON (we treat WMON as MON collateral as well)
  const symbol = token?.symbol;
  if (symbol === 'USDC') return 'USDC-LVUSD';
  if (symbol === 'MON' || symbol === 'WMON') return 'MON-LVMON';
  
return null;
};

const getConfirmTradeIntroStorageKey = (variant: ConfirmTradeIntroVariant) =>
  getLocalStorageKey(`trade.confirm-intro.${variant}`);

const hasSeenConfirmTradeIntro = (variant: ConfirmTradeIntroVariant) => {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(getConfirmTradeIntroStorageKey(variant)) === '1';
  } catch {
    return true;
  }
};

const markConfirmTradeIntroSeen = (variant: ConfirmTradeIntroVariant) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(getConfirmTradeIntroStorageKey(variant), '1');
  } catch {
    // ignore
  }
};

export const TradeModuleConfirm = ({
  children,
}: {
  children: ReactElement;
}) => {
  const [isOpen, setOpen] = useState(false);
  const [introVariant, setIntroVariant] =
    useState<ConfirmTradeIntroVariant | null>(null);

  const orderType = useOrderType((s) => s._persistValue);
  const isLimitOrder = orderType !== OrderType.MARKET;
  const { setIsTradeModuleModalOpen, setIsConfirmTradeIntroOpen } =
    useTradeModuleContext();
  const payToken = useCollateralToken((s) => s.useCollateralTokenValue());

  /**
   * Important: don't immediately flip `isConfirmTradeIntroOpen` to false during the same
   * click that closes the intro modal. Otherwise that click can be interpreted as an
   * "outside interaction" by the underlying TradeModuleModal drawer and close it.
   */
  const closeIntroUiSafely = () => {
    setIntroVariant(null);
    // keep the underlying drawer protected for this event tick
    setTimeout(() => setIsConfirmTradeIntroOpen?.(false), 0);
  };

  const onReceiptSettledCallBack = () => {
    setOpen(false);
    setIsTradeModuleModalOpen?.(false);
    setIsConfirmTradeIntroOpen?.(false);
  };

  const {
    write: createMarketOrder,
    isPending: isCreateMarketOrderPending,
  } = useOpenMarketTrade({
    onReceiptSettled: onReceiptSettledCallBack, // init the receipt settle toast display
  });

  const {
    writeContract: createLimitOrder,
    isPending: isCreateLimitOrderPending,
  } = useOpenLimitOrder({
    onReceiptSettled: onReceiptSettledCallBack, // init the receipt settle toast display
  });

  const isPending = isLimitOrder
    ? isCreateLimitOrderPending
    : isCreateMarketOrderPending;

  const createTrade = () => {
    if (isPending) return;
    isLimitOrder ? createLimitOrder() : createMarketOrder();
  };

  const onTradeClick = () => {
    if (isPending) return;

    const variant = getConfirmTradeIntroVariant(payToken);
    if (variant && !hasSeenConfirmTradeIntro(variant)) {
      setIntroVariant(variant);
      setIsConfirmTradeIntroOpen?.(true);
      
return;
    }

    createTrade();
  };

  return (
    <Drawer.Root
      direction="right"
      open={isOpen}
      onOpenChange={onTradeClick}
      modal={true} // allow scrolling in mobile
      lazyMount
      unmountOnExit
      closeOnInteractOutside={false} //
    >
      <ConfirmTradeIntroModal
        variant={introVariant}
        onClose={() => {
          closeIntroUiSafely();
        }}
        onConfirm={() => {
          if (!introVariant) return;
          markConfirmTradeIntroSeen(introVariant);
          closeIntroUiSafely();
          createTrade();
        }}
      />
      <Drawer.Trigger asChild>
        {cloneElement(children, {
          loading: isPending,
        })}
      </Drawer.Trigger>
      <Portal>
        {/* <Drawer.Backdrop /> */}
        <Drawer.Positioner>
          <Drawer.Content
            display="flex"
            flexDir="column"
            data-cy="TradeConfirmModule"
          >
            <Drawer.Header asChild>
              <Head />
            </Drawer.Header>
            <Drawer.Body
              display="flex"
              flexDir="column"
              p={0}
              flex={1}
              bg="black.background"
            >
              <Grid
                gridTemplateColumns="1fr 1fr 1fr"
                px={4}
                py={5}
                bg="black.background"
                boxShadow="pressingLv2"
              >
                {/* <Collateral />
                <Leverage />
                <PositionSize /> */}
              </Grid>

              {/* <ConfirmChart /> */}

              <Grid
                gridTemplateAreas={`
                  "mark entry entry"
                  "tp sl liq"
                `}
                gridTemplateColumns="1fr 1fr 1fr"
                gap={5}
                px={4}
                py={5}
                bg="black.background"
                boxShadow="pressingLv2"
              >
                {/* <MarkPrice gridArea="mark" />
                <EntryPrice gridArea="entry" />
                <TakeProfitAt />
                <StopLossAt />
                <LiqPrice /> */}
              </Grid>

              <Spacer />
            </Drawer.Body>
            <Drawer.Footer
              position="sticky"
              bottom={0}
              p={4}
              bg="black.background"
              display="block"
            >
              {/* <HStack justifyContent="space-between" mb="16px">
                <Text textStyle="text.default" color="greyWhite.lightGrey3">
                  {t('common.Fees')}
                </Text>
              </HStack> */}
              {/* <ConfirmTradeButton
                loading={isPending}
                onClick={onConfirmClick}
                setModalOpen={setOpen}
              /> */}
              <MarketStatusMessageFooter />
            </Drawer.Footer>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};

const Head = forwardRef((p: HstackProps, ref: Ref<HTMLDivElement>) => {
  const { t } = useTranslation();
  const isLong = useIsLong((s) => s._persistValue);
  const selectedTradingPair = useTradingPair((s) => s.useTradingPair());

  return (
    <HStack ref={ref} {...p}>
      <Drawer.CloseTrigger>
        <Icon w="24px" h="24px">
          <CaretLeft />
        </Icon>
      </Drawer.CloseTrigger>
      <Drawer.Title>
        {t('common.Confirm {longOrShort} {token}', {
          longOrShort: isLong ? 'Long' : 'Short',
          token: selectedTradingPair?.baseSymbol,
        })}
      </Drawer.Title>
      <Spacer />
      <Drawer.CloseTrigger>
        <Icon w="24px" h="24px">
          <X />
        </Icon>
      </Drawer.CloseTrigger>
    </HStack>
  );
});

Head.displayName = 'Head';

const MarketStatusMessageFooter = () => {
  const status = useMarketStatus();
  const isLong = useIsLong((s) => s._persistValue);

  return (
    <Box
      mt={3}
      _empty={{
        mt: 0,
      }}
    >
      <MarketStatusMessage status={status} isLong={isLong} />
    </Box>
  );
};
